import { useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { usePopper } from 'react-popper';

import { HEADER_Z_INDEX } from 'ds/constants';

import DateRange from './DateRange';
import TableSearchableOneOfFilter from './TableSearchableOneOfFilter';
import { HEADER_INPUT_SIZE } from './constants';
import { FilterState, Filters, Row, SetFilter } from './types';
import Card from '../Card';
import Checkbox from '../Checkbox';
import Layout from '../Layout';
import Text from '../Text';
import TextButton from '../TextButton';
import { useOnClickOutside } from '../helpers';
import MoneyInput from '../inputs/MoneyInput';
import NumberInput from '../inputs/NumberInput';
import TextInput from '../inputs/TextInput';

interface Props<T> {
  searchInputOpen: boolean;
  filterInputOpen: boolean;
  dateRangeOpen: boolean;
  moneyRangeOpen: boolean;
  numberRangeOpen: boolean;
  filters: Filters<T>;
  filter?: FilterState;
  setFilter: SetFilter;
  alignRight: boolean;
  headerWidth: number;
  hideFilterInputs: VoidFunction;
}

const FilterControls = <T extends Row>({
  searchInputOpen,
  filterInputOpen,
  dateRangeOpen,
  moneyRangeOpen,
  numberRangeOpen,
  filter,
  filters,
  setFilter,
  alignRight,
  headerWidth,
  hideFilterInputs
}: Props<T>) => {
  const oneOfValues = filter?.oneOf || [];

  const [referenceElement, setReferenceElement] = useState<HTMLElement | null>(null);
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(null);
  const searchFilterRef = useRef<HTMLDivElement>(null);
  const [calendarStartElement, setCalendarStartElement] = useState<HTMLDivElement | null>(null);
  const [calendarEndElement, setCalendarEndElement] = useState<HTMLDivElement | null>(null);

  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    placement: alignRight ? 'bottom-end' : 'bottom-start',
    strategy: 'fixed',
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: alignRight ? [8, 8] : [8, -18]
        }
      },
      {
        name: 'preventOverflow',
        options: {
          padding: 12,
          altAxis: true
        }
      }
    ]
  });

  useOnClickOutside(searchFilterRef, event => {
    const target = event.target as HTMLElement;
    if (popperElement && popperElement.contains(target)) {
      return;
    }
    // Typeahead results are portaled to document.body (outside the popper node).
    if (target.closest?.('.Typeahead-results')) {
      return;
    }
    if (
      (calendarStartElement && calendarStartElement.contains(event.target as HTMLElement)) ||
      (calendarEndElement && calendarEndElement.contains(event.target as HTMLElement))
    ) {
      return;
    }
    hideFilterInputs();
  });

  const renderFilterContent = () => {
    if (searchInputOpen) {
      return (
        <TextInput
          label="Search"
          value={filter?.substring}
          size={HEADER_INPUT_SIZE}
          onChange={({ value }) => {
            setFilter({ substring: value });
          }}
          onClear={() => setFilter({ substring: '' })}
          autoFocus
        />
      );
    } else if (filterInputOpen && filters.searchableOneOf) {
      return (
        <TableSearchableOneOfFilter
          options={filters.searchableOneOf.options}
          oneOfValues={oneOfValues}
          setFilter={setFilter}
        />
      );
    } else if (filterInputOpen && filters.oneOf) {
      return (
        <Card size="sm">
          {filters.oneOf.options.map(({ label, value }, index) => (
            <Layout key={value.toString()} marginTop={index === 0 ? undefined : 8}>
              <Checkbox
                value={oneOfValues.includes(value) ? 'checked' : 'off'}
                label={label}
                onChange={({ value: checkboxValue }) => {
                  if (checkboxValue === 'checked') {
                    setFilter({ oneOf: [...oneOfValues, value] });
                  } else {
                    setFilter({ oneOf: oneOfValues.filter(v => v !== value) });
                  }
                }}
                size={HEADER_INPUT_SIZE}
              />
            </Layout>
          ))}
          <Layout marginTop={8} direction="row" justify="space-between" gap={24}>
            <TextButton
              scale
              textSize="body3"
              text="Clear"
              color="gray-600"
              paddingY={false}
              onClick={() => setFilter({ oneOf: [] })}
            />
            <TextButton
              scale
              textSize="body3"
              text="Select all"
              color="blue-500"
              paddingY={false}
              onClick={() => setFilter({ oneOf: filters?.oneOf?.options.map(o => o.value) || [] })}
            />
          </Layout>
        </Card>
      );
    } else if (dateRangeOpen) {
      return (
        <DateRange
          startDate={filter?.startDate}
          endDate={filter?.endDate}
          reset={() => setFilter({ startDate: undefined, endDate: undefined })}
          setStartDate={startDate => setFilter({ startDate: startDate || undefined, endDate: filter?.endDate })}
          setEndDate={endDate => setFilter({ startDate: filter?.startDate, endDate: endDate || undefined })}
          setCalendarStartElement={setCalendarStartElement}
          setCalendarEndElement={setCalendarEndElement}
          placement={alignRight ? 'bottom-end' : 'bottom'}
        />
      );
    } else if (moneyRangeOpen) {
      return (
        <Layout
          __ref={searchFilterRef}
          position="absolute"
          zIndex={HEADER_Z_INDEX}
          {...(alignRight ? { right: headerWidth } : { left: headerWidth - 16 })}
          top={0}
          __className="Table-numberRangeFilter"
          padding={4}
          direction="row"
          align="center"
          color="white"
        >
          <MoneyInput
            label="min"
            required={false}
            value={filter?.minValue}
            size={HEADER_INPUT_SIZE}
            onChange={({ value }) => {
              setFilter({ minValue: value, maxValue: filter?.maxValue });
            }}
            onClear={() => setFilter({ minValue: undefined, maxValue: filter?.maxValue })}
            autoFocus
          />
          <Text size="body2" scale>
            {' '}
            -{' '}
          </Text>
          <MoneyInput
            label="max"
            required={false}
            value={filter?.maxValue}
            size={HEADER_INPUT_SIZE}
            onChange={({ value }) => {
              setFilter({ minValue: filter?.minValue, maxValue: value });
            }}
            onClear={() => setFilter({ minValue: filter?.minValue, maxValue: undefined })}
          />
        </Layout>
      );
    } else if (numberRangeOpen) {
      return (
        <Layout
          __ref={searchFilterRef}
          position="absolute"
          zIndex={HEADER_Z_INDEX}
          {...(alignRight ? { right: headerWidth } : { left: headerWidth - 40 })}
          top={0}
          __className="Table-numberRangeFilter"
          padding={4}
          direction="row"
          align="center"
          color="white"
        >
          <NumberInput
            label="min"
            required={false}
            value={filter?.minValue}
            size={HEADER_INPUT_SIZE}
            onChange={({ value }) => {
              setFilter({ minValue: value, maxValue: filter?.maxValue });
            }}
            onClear={() => setFilter({ minValue: undefined, maxValue: filter?.maxValue })}
            autoFocus
          />
          <Text size="body2" scale>
            {' '}
            -{' '}
          </Text>
          <NumberInput
            label="max"
            required={false}
            value={filter?.maxValue}
            size={HEADER_INPUT_SIZE}
            onChange={({ value }) => {
              setFilter({ minValue: filter?.minValue, maxValue: value });
            }}
            onClear={() => setFilter({ minValue: filter?.minValue, maxValue: undefined })}
          />
        </Layout>
      );
    } else {
      return null;
    }
  };

  return (
    <>
      <div ref={setReferenceElement}>
        <div ref={searchFilterRef}>
          {(searchInputOpen || filterInputOpen || dateRangeOpen || moneyRangeOpen || numberRangeOpen) &&
            ReactDOM.createPortal(
              <div
                ref={setPopperElement}
                style={{
                  ...styles.popper,
                  zIndex: HEADER_Z_INDEX + 1, // Ensure it's above header
                  ...(filterInputOpen && filters.searchableOneOf ? { width: 400 } : {})
                }}
                {...attributes.popper}
              >
                {renderFilterContent()}
              </div>,
              document.body
            )}
        </div>
      </div>
    </>
  );
};

export default FilterControls;
